import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DescriptionItem,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  StatusPill,
  Textarea
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import {
  IconAlert,
  IconArrowLeft,
  IconCheck,
  IconCourier,
  IconLink,
  IconNote,
  IconRefresh
} from '../../components/admin/icons';
import {
  actionLabels,
  actionTone,
  billedAsLabels,
  billedAsTone,
  caseStatusLabels,
  caseStatusTone,
  createdByLabels,
  createdByTone,
  emailStatusLabels,
  emailStatusTone,
  formatDate,
  formatDateTime,
  inferBilledAs,
  issueTypeLabels,
  paymentStatusTone
} from '../../components/admin/labels';

const statusOptions = [
  'submitted',
  'evidence_prepared',
  'merchant_reviewed',
  'merchant_responded',
  'closed',
  'no_merchant_response',
  'system_error'
];

const CaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();

  const [caseDoc, setCaseDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [magicLink, setMagicLink] = useState(null);
  const [quality, setQuality] = useState('pending');
  const [savingQuality, setSavingQuality] = useState(false);

  const fetchCase = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/cases/${caseId}`);
      setCaseDoc(data);
      setStatus(data.status);
      setQuality(data.qualityStatus || 'pending');
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this case.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const handleStatusSave = async () => {
    const ok = await confirm({
      title: 'Change case status',
      message: `Set case ${caseId} to "${caseStatusLabels[status] || status}"?`,
      detail: 'The customer is not notified by a status change on its own.',
      confirmLabel: 'Change status'
    });
    if (!ok) return;

    setSavingStatus(true);
    try {
      await api.patch(`/api/cases/${caseId}/status`, { status });
      await fetchCase();
      toast('Case status updated.', { tone: 'success' });
    } catch (err) {
      toast(err.response?.data?.error || 'Could not update the status.', { tone: 'error' });
    } finally {
      setSavingStatus(false);
    }
  };

  const handleQualitySave = async () => {
    setSavingQuality(true);
    try {
      await api.patch(`/api/admin/cases/${caseId}/quality`, { qualityStatus: quality });
      await fetchCase();
      toast('Case quality updated.', { tone: 'success' });
    } catch (err) {
      toast(err.response?.data?.error || 'Could not update case quality.', { tone: 'error' });
    } finally {
      setSavingQuality(false);
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) {
      setNoteError('Write a note before saving.');
      return;
    }
    setNoteError('');
    setSavingNote(true);
    try {
      await api.post(`/api/cases/${caseId}/notes`, { note: note.trim() });
      setNote('');
      await fetchCase();
      toast('Note added.', { tone: 'success' });
    } catch (err) {
      setNoteError(err.response?.data?.error || 'Could not add this note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleResendMagicLink = async () => {
    try {
      const { data } = await api.post(`/api/admin/cases/${caseId}/resend-magic-link`);
      setMagicLink(data);
      await fetchCase();
    } catch (err) {
      toast(err.response?.data?.error || 'Could not generate a link.', { tone: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-40" />
        <Card>
          <CardBody className="space-y-4">
            <Skeleton className="h-5 w-56" />
            <Skeleton className="h-28 w-full" />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (error || !caseDoc) {
    return (
      <Card>
        <EmptyState
          icon={IconAlert}
          title="Case not found"
          description={error || 'This case may have been removed.'}
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate('/admin/cases')}>
              <IconArrowLeft size={15} />
              Back to cases
            </Button>
          }
        />
      </Card>
    );
  }

  const delivery = caseDoc.deliveryDetails || {};
  const merchant = caseDoc.merchant || {};
  const events = caseDoc.ship24?.events || [];
  const files = caseDoc.evidence?.files || [];
  const statusChanged = status !== caseDoc.status;

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate('/admin/cases')}
            className="mb-2 inline-flex items-center gap-1.5 rounded text-sm font-medium text-ink-500 transition-colors hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
          >
            <IconArrowLeft size={15} />
            Back to cases
          </button>
          <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900">
            Case {caseDoc.caseId}
          </h2>
          <p className="mt-1 text-sm text-ink-500">
            {issueTypeLabels[caseDoc.issueType] || caseDoc.issueType} · submitted{' '}
            {formatDate(caseDoc.submittedAt)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={caseStatusTone[caseDoc.status] || 'neutral'}>
            {caseStatusLabels[caseDoc.status] || caseDoc.status}
          </StatusPill>
          <Button variant="ghost" size="sm" onClick={fetchCase}>
            <IconRefresh size={15} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status update */}
      <Card>
        <CardHeader
          title="Update status"
          description="Where this case sits in its lifecycle."
        />
        <CardBody>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Status" htmlFor="case-status" required className="w-full max-w-xs">
              <Select id="case-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {caseStatusLabels[option]}
                  </option>
                ))}
              </Select>
            </Field>
            <Button onClick={handleStatusSave} loading={savingStatus} disabled={!statusChanged}>
              <IconCheck size={16} />
              Save status
            </Button>
            {statusChanged && (
              <p className="pb-2 text-sm text-ink-500">
                Changing from {caseStatusLabels[caseDoc.status] || caseDoc.status}
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Acquisition — customer-submitted cases only */}
      {caseDoc.createdBy !== 'merchant' && (
        <Card>
          <CardHeader
            title="Acquisition"
            description="Where this report came from, and whether it counts as a valid case in the acquisition report."
          />
          <CardBody className="space-y-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <DescriptionItem label="Source">
                {caseDoc.acquisition?.source || 'Not tracked'}
              </DescriptionItem>
              <DescriptionItem label="Campaign">{caseDoc.acquisition?.campaign || '—'}</DescriptionItem>
              <DescriptionItem label="Medium / content">
                {[caseDoc.acquisition?.medium, caseDoc.acquisition?.content].filter(Boolean).join(' / ') || '—'}
              </DescriptionItem>
              <DescriptionItem label="Landing page">
                {caseDoc.acquisition?.lastTouch?.landingPage || '—'}
              </DescriptionItem>
              <DescriptionItem label="First touch">
                {caseDoc.acquisition?.firstTouch?.source
                  ? [caseDoc.acquisition.firstTouch.source, caseDoc.acquisition.firstTouch.campaign].filter(Boolean).join(' / ')
                  : caseDoc.acquisition ? 'direct' : '—'}
              </DescriptionItem>
              <DescriptionItem label="Ad cookie consent">
                {caseDoc.acquisition?.consent?.state || '—'}
              </DescriptionItem>
            </dl>
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Case quality" htmlFor="case-quality" className="w-full max-w-xs">
                <Select id="case-quality" value={quality} onChange={(e) => setQuality(e.target.value)}>
                  <option value="pending">Pending review</option>
                  <option value="valid">Valid</option>
                  <option value="duplicate">Duplicate</option>
                  <option value="invalid">Invalid</option>
                </Select>
              </Field>
              <Button
                onClick={handleQualitySave}
                loading={savingQuality}
                disabled={quality === (caseDoc.qualityStatus || 'pending')}
              >
                <IconCheck size={16} />
                Save quality
              </Button>
              {caseDoc.qualityUpdatedAt && (
                <p className="pb-2 text-sm text-ink-500">Last changed {formatDateTime(caseDoc.qualityUpdatedAt)}</p>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Customer */}
        <Card>
          <CardHeader title="Customer" />
          <CardBody>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DescriptionItem label="Name">{caseDoc.customer?.name || '—'}</DescriptionItem>
              <DescriptionItem label="Email">{caseDoc.customer?.email || '—'}</DescriptionItem>
              <DescriptionItem label="Phone">{caseDoc.customer?.phone || '—'}</DescriptionItem>
              <DescriptionItem label="SMS updates">
                {caseDoc.smsOptIn?.optedIn ? (
                  <StatusPill tone={paymentStatusTone[caseDoc.smsOptIn.paymentStatus] || 'neutral'}>
                    {caseDoc.smsOptIn.paymentStatus || 'opted in'}
                  </StatusPill>
                ) : (
                  'Not opted in'
                )}
              </DescriptionItem>
            </dl>
          </CardBody>
        </Card>

        {/* Delivery */}
        <Card>
          <CardHeader title="Delivery" />
          <CardBody>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DescriptionItem label="Tracking">{delivery.trackingNumber || '—'}</DescriptionItem>
              <DescriptionItem label="Courier">{delivery.courier || '—'}</DescriptionItem>
              <DescriptionItem label="Merchant">{delivery.merchantName || '—'}</DescriptionItem>
              <DescriptionItem label="Order">{delivery.orderNumber || '—'}</DescriptionItem>
              <DescriptionItem label="Postcode">{delivery.postcode || '—'}</DescriptionItem>
              <DescriptionItem label="Merchant email">{delivery.merchantEmail || '—'}</DescriptionItem>
            </dl>
          </CardBody>
        </Card>
      </div>

      {/* Merchant response */}
      <Card>
        <CardHeader
          title="Merchant"
          description="What the retailer has done with this case."
          actions={
            <Button variant="secondary" size="sm" onClick={handleResendMagicLink}>
              <IconLink size={15} />
              Resend magic link
            </Button>
          }
        />
        <CardBody>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <DescriptionItem label="Account">
              {merchant.merchantId?._id ? (
                <button
                  type="button"
                  onClick={() => navigate(`/admin/merchants/${merchant.merchantId._id}`)}
                  className="rounded text-left font-600 text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                >
                  {merchant.merchantId.name}
                </button>
              ) : (
                '—'
              )}
            </DescriptionItem>
            <DescriptionItem label="Created by">
              <StatusPill tone={createdByTone[caseDoc.createdBy] || 'neutral'}>
                {createdByLabels[caseDoc.createdBy] || 'Customer'}
              </StatusPill>
            </DescriptionItem>
            <DescriptionItem label="Action">
              {merchant.action ? (
                <StatusPill tone={actionTone[merchant.action] || 'neutral'}>
                  {actionLabels[merchant.action] || merchant.action}
                </StatusPill>
              ) : (
                'No action yet'
              )}
            </DescriptionItem>
            <DescriptionItem label="Action taken">
              {merchant.actionTakenAt ? formatDateTime(merchant.actionTakenAt) : '—'}
            </DescriptionItem>
            <DescriptionItem label="Email status">
              {caseDoc.emailStatus?.status ? (
                <StatusPill tone={emailStatusTone[caseDoc.emailStatus.status] || 'neutral'}>
                  {emailStatusLabels[caseDoc.emailStatus.status] || caseDoc.emailStatus.status}
                </StatusPill>
              ) : (
                'Not sent'
              )}
            </DescriptionItem>
            {merchant.rejectionReason && (
              <div className="sm:col-span-3">
                <DescriptionItem label="Rejection reason">{merchant.rejectionReason}</DescriptionItem>
              </div>
            )}
            <DescriptionItem label="Counted for billing">
              {caseDoc.billingCountedAt ? formatDateTime(caseDoc.billingCountedAt) : 'Not yet'}
            </DescriptionItem>
            <DescriptionItem label="Billed as">
              {(() => {
                const billedAs = inferBilledAs(caseDoc);
                return billedAs ? (
                  <StatusPill tone={billedAsTone[billedAs] || 'neutral'}>
                    {billedAsLabels[billedAs] || billedAs}
                  </StatusPill>
                ) : (
                  '—'
                );
              })()}
            </DescriptionItem>
            <DescriptionItem label="Pay-per-case paid">
              {caseDoc.payPerCasePaidAt ? formatDateTime(caseDoc.payPerCasePaidAt) : '—'}
            </DescriptionItem>
          </dl>
        </CardBody>
      </Card>

      {/* Evidence */}
      <Card>
        <CardHeader title="Evidence" description="What the customer submitted." />
        <CardBody className="space-y-4">
          {caseDoc.evidence?.comments ? (
            <blockquote className="rounded-r-lg border-l-[3px] border-ink-300 bg-ink-50 px-4 py-3 text-sm italic leading-relaxed text-ink-700">
              &ldquo;{caseDoc.evidence.comments}&rdquo;
            </blockquote>
          ) : (
            <p className="text-sm text-ink-500">No comments were added.</p>
          )}

          {caseDoc.dor?.signed && (
            <div className="rounded-lg bg-success-50 px-4 py-3 ring-1 ring-inset ring-success-100">
              <p className="text-sm font-600 text-success-700">Declaration of non-receipt signed</p>
              <p className="mt-1 text-xs text-success-700/80">
                {formatDateTime(caseDoc.dor.timestamp)} · IP {caseDoc.dor.ipAddress || 'unknown'}
              </p>
            </div>
          )}

          {files.length > 0 ? (
            <ul className="space-y-2">
              {files.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between gap-3 rounded-lg border border-ink-200 px-3.5 py-2.5"
                >
                  <span className="min-w-0 truncate text-sm text-ink-700">{file.filename}</span>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-sm font-600 text-brand-700 hover:underline"
                  >
                    Open
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500">No files were uploaded.</p>
          )}
        </CardBody>
      </Card>

      {/* Courier tracking */}
      {events.length > 0 && (
        <Card>
          <CardHeader title="Courier tracking" description="From Ship24." />
          <CardBody>
            <ol className="space-y-3">
              {events.map((event, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                    <IconCourier size={13} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-500 text-ink-900">
                      {event.status}
                      {event.location ? ` — ${event.location}` : ''}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">{formatDateTime(event.timestamp)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>
      )}

      {/* Admin notes */}
      <Card>
        <CardHeader title="Admin notes" description="Internal only — never shown to the customer." />
        <CardBody className="space-y-4">
          {caseDoc.adminNotes?.length ? (
            <ul className="space-y-2.5">
              {caseDoc.adminNotes.map((entry, index) => (
                <li key={index} className="rounded-xl border border-ink-200 px-4 py-3">
                  <p className="text-sm text-ink-800">{entry.note}</p>
                  <p className="mt-1.5 text-xs text-ink-500">
                    {entry.addedBy?.name || 'Admin'} · {formatDateTime(entry.addedAt)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500">No notes on this case yet.</p>
          )}

          <div className="border-t border-ink-100 pt-4">
            <Field label="Add a note" htmlFor="new-note" required error={noteError}>
              <Textarea
                id="new-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What happened, what was agreed, what happens next…"
              />
            </Field>
            <Button onClick={handleAddNote} loading={savingNote} className="mt-3">
              <IconNote size={16} />
              Save note
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Magic link result */}
      <Modal
        open={Boolean(magicLink)}
        onClose={() => setMagicLink(null)}
        title="Merchant link"
        description="Share this with the merchant. It replaces any previous link for this case."
        size="sm"
        footer={
          <Button variant="secondary" onClick={() => setMagicLink(null)}>
            Done
          </Button>
        }
      >
        <Input
          readOnly
          value={magicLink?.magicLink || ''}
          onClick={(e) => e.target.select()}
          className="font-mono text-xs"
        />
        <p className="mt-2 text-xs text-ink-500">
          Expires {magicLink?.expiresAt ? formatDateTime(magicLink.expiresAt) : 'shortly'}.
        </p>
      </Modal>
    </div>
  );
};

export default CaseDetail;
